import { graphql } from "../generated"

export const GetPresignedUrlForXml = graphql(`
  query GetPresignedUrlForXml($fileStore: String!, $key: String!) {
    getPresignedUrl(fileStore: $fileStore, key: $key)
  }
`)

export const listFeatureFlags = graphql(`
  query listFeatureFlags {
    getFeatureFlags
  }
`)

export const productForm = graphql(`
  query productForm(
    $certificateType: CertificateType
    $country: String
    $commodity: String
  ) {
    productForm(
      input: {
        certificateType: $certificateType
        commodity: $commodity
        country: $country
      }
    ) {
      type
      required
      readonly
      numberOfLines
      nested {
        type
        id
        collection
      }
      label
      id
      options {
        value
        label
      }
      description
      collection
      numberConstrain {
        step
        min
        max
      }
      filter {
        attribute
        value
      }
      charLength
    }
  }
`)

export const queryExportCertificateBase = graphql(`
  query queryExportCertificateBase($id: ID!) {
    exportCertificateRequest(id: $id) {
      id
      commodity
      status
      certificateType
      destination {
        id
      }
    }
  }
`)

export const queryExportCertificateDetails = graphql(`
  query queryExportCertificateDetails($id: ID!) {
    exportCertificateRequest(id: $id) {
      id
      status
      statusLabel
      certificateNumber
      statusNotes
      replacing {
        id
        certificateNumber
      }
      replacedBy {
        id
        status
        certificateNumber
      }
    }
  }
`)

export const queryReferencedDocument = graphql(`
  query referencedDocument($id: ID!) {
    referencedDocument(id: $id) {
      uploadInProgress
      id
      filename
    }
  }
`)

export const queryDownloadReferencedDocument = graphql(`
  query downloadSupportingDocument($id: ID!) {
    downloadReferencedDocument(id: $id) {
      documentName
      url
    }
  }
`)

export const queryCoreReferencedDocument = graphql(`
  query coreReferencedDocument($id: ID!) {
    coreReferencedDocument(id: $id) {
      uploadInProgress
      id
      filename
    }
  }
`)

export const queryDownloadCoreReferencedDocument = graphql(`
  query downloadCoreReferencedDocument($id: ID!) {
    downloadCoreReferencedDocument(id: $id) {
      documentName
      url
    }
  }
`)

export const queryProduct = graphql(`
  query GetProduct($id: ID!) {
    product(id: $id) {
      id
      premise {
        id
        name
      }
      productNumber
      modifiable
      description
      exporterReference
      wine {
        totalPackagedLitres
        availableLitres
        exportedLitres
        shippingFormat
        EUCategory
      }
      eligibilityStatus {
        market
        status
        expiryDate
      }
    }
  }
`)

export const querySimpleProduct = graphql(`
  query GetSimpleProduct($id: ID!) {
    product(id: $id) {
      id
      wine {
        vintage
        alternateWineNames
      }
    }
  }
`)

export const queryValidate = graphql(`
  query queryValidate($id: ID!) {
    validate(id: $id) {
      error
      path
      type
    }
  }
`)

export const queryValidateProduct = graphql(`
  query queryValidateProduct($id: ID, $product: B2GProductInput!) {
    validateProduct(id: $id, product: $product) {
      error
      path
      type
    }
  }
`)

export const querySfExporter = graphql(`
  query querySfExporter($id: ID!, $certificateType: CertificateType) {
    sfExporter(id: $id, certificateType: $certificateType) {
      addresses {
        addressType
        city
        country
        street
        postalCode
        isPrimary
      }
      nzbn
      name
      id
      registrationNumber
      contacts {
        emails {
          emailAddress
          isPrimary
        }
        name
        phones {
          telephoneNumber
          isPrimary
        }
        role
      }
      identifiers {
        value
        id
        description
      }
    }
  }
`)

export const queryAvailableProcesses = graphql(`
  query getAvailableProcesses($productId: ID!) {
    availableProcesses(productId: $productId) {
      description
      id
    }
  }
`)

export const queryProductProcessesSensoryEvaluation = graphql(`
  query queryProductProcessesSensoryEvaluation($productId: ID!) {
    productProcesses(
      input: { productId: $productId, filter: { processType: SENSORY } }
    ) {
      processId
      processType
      taskId
      attributes {
        ... on SensoryProcessRequest {
          assessmentDate
          reference
          disputed
          disputedReason
          id
          productId
          winemaker {
            personName
          }
          comment
        }
      }
    }
  }
`)

export const queryProductProcessesChemistryTest = graphql(`
  query queryProductProcessesChemistryTest($productId: ID!) {
    productProcesses(
      input: { productId: $productId, filter: { processType: CHEMISTRY } }
    ) {
      processId
      processType
      taskId
      attributes {
        ... on ChemistryTestResponse {
          requestedDate
          status
          cancelledReason
          firstResultReportedDate
          receipt {
            laboratoryReference
            receiptDate
            sampleSuitable
            unsuitableReason
          }
          request {
            expectedAlcoholContent
            laboratory
            packagingDate
            packagingReference
            sampleCollectionDate
            sampleReference
            testRequired
            wineTestReference
          }
        }
      }
    }
  }
`)

export const queryProductProcessBottlingRuns = graphql(`
  query QueryProductProcessBottlingRuns($productId: ID!) {
    productProcesses(
      input: { productId: $productId, filter: { processType: BOTTLING_RUN } }
    ) {
      attributes {
        ... on Packaging {
          wsmp
          exportedLitres
          confirmation {
            confirmed
            disputeReason
            disputeResolution
          }
          availableLitres
          runs {
            ... on BottlingRunData {
              id
              bottleVolume
              bottlesPerCase
              cases
              date
              extraBottles
              litres
              reference
            }
          }
          status
          totalPackagedLitres
        }
      }
      confirmationTaskId
      requestTaskId
      taskId
      processType
      processId
    }
  }
`)

export const queryProductProcessFillingRuns = graphql(`
  query QueryProductProcessFillingRuns($productId: ID!) {
    productProcesses(
      input: { productId: $productId, filter: { processType: FLEXI_FILLING } }
    ) {
      attributes {
        ... on Packaging {
          wsmp
          exportedLitres
          confirmation {
            confirmed
            disputeReason
            disputeResolution
          }
          availableLitres
          runs {
            ... on FlexiFillingRunData {
              date
              id
              litres
              reference
            }
          }
          status
          totalPackagedLitres
        }
      }
      confirmationTaskId
      requestTaskId
      taskId
      processType
      processId
    }
  }
`)

export const queryProductDeclaration = graphql(`
  query queryProductDeclaration($commodity: String!) {
    productFormlet(input: { commodity: $commodity }) {
      declarations {
        id
        type
        fields {
          market {
            id
            value
            label
          }
          text {
            id
            type
            label
          }
          eligibilityCriteria {
            id
            value
          }
        }
      }
    }
  }
`)

export const queryCertificates = graphql(`
  query FindAvailableTemplates($commodity: String!, $countryCode: String!) {
    availableTemplates(commodity: $commodity, countryCode: $countryCode) {
      id
      code
      title
      exportCertificateType
      commodity
      destinationMarket
      displayedDescription
      minorVersion
      majorVersion
    }
  }
`)

export const queryProductProcessWinemaking = graphql(`
  query queryProductProcessWinemaking($id: ID!) {
    productProcesses(
      input: { filter: { processType: WINE_MAKING }, productId: $id }
    ) {
      attributes {
        ... on Winemaking {
          wsmp
          confirmations {
            status
            market
            disputedResolution
            disputedReason
            confirmed
          }
        }
      }
      confirmationTaskId
      requestTaskId
      taskId
      processType
      processId
    }
  }
`)

export const queryUserProfile = graphql(`
  query queryTradecertUserProfile($id: ID, $certificateType: CertificateType) {
    tradecertUserProfile(id: $id, certificateType: $certificateType) {
      userPrimaryPhone
      registrationNumber
      registrationIdentifier
      organisationPrimaryAddress {
        street
        postcode
        countryId
        city
        countryName
        typeCode
      }
      organisationName
      organisationPartyId
      userEmail
      userName
    }
  }
`)

export const downloadProductData = graphql(`
  query downloadProducts($startDate: AWSDateTime, $endDate: AWSDateTime) {
    downloadProducts(startDate: $startDate, endDate: $endDate) {
      url
      filename
    }
  }
`)

export const downloadExportCerificatesData = graphql(`
  query downloadExportCertificates(
    $startDate: AWSDateTime
    $endDate: AWSDateTime
  ) {
    downloadExportCertificates(startDate: $startDate, endDate: $endDate) {
      url
      filename
    }
  }
`)

export const getTransferRequestTemplateInfo = graphql(`
  query GetTransferRequestTemplateInfo {
    getDfeTransferRequestTemplateInfo {
      templateId
      templateRevisionId
    }
  }
`)

export const getTransferRequestDetails = graphql(`
  query GetTransferRequestDetails($id: ID!) {
    getTransferRequestDetails(input: { transferId: $id }) {
      certificateNumber
      requiresAttention
      id
      version
      commodity {
        id
      }
      consignor {
        id
        name
        registrationId
      }
      consignee {
        id
        name
        registrationId
      }
      template {
        id
      }
      templateId
      destinationCountry {
        id
      }
      status {
        code
        label
      }
      attributes {
        address {
          id
          name
        }
        phytoSchemeControl {
          id
          name
        }
        phytoCertSubType {
          id
          name
        }
        phytoStatementOfRelabelling
        inspectionDate
        letterOfCreditRefCode {
          id
          name
        }
        numberOfPackagesContainers {
          id
          name
        }
        phytoPackhouse
        phytoLotRefNumber
        importPermitNumbers
        orgCertRefCode
        issuingAuthority {
          id
          name
        }
        halalAuditor {
          id
          name
        }
        brandName {
          id
          name
        }
      }
      reasonForStatusChange
      eligibility_declaration
      urgency {
        id
        name
      }
      unofficialInformation
      thirdPartyConsignor {
        id
        name
        registrationId
      }
      uniqueConsignmentReference
      transport {
        carrierName
        transportMode {
          id
          name
        }
        finalDestination {
          id
          name
        }
        departureDate
        loadingPort {
          id
          name
        }
        localCarrier
        localCarrierReference
        conveyanceReference
      }
      products {
        itemDescription {
          id
          name
        }
        id
        exhausted
        sequenceNumber
        processes {
          endDate
          foreignPremises
          foreignPremisesIdentifier
          id
          overrideProcessDate
          premises {
            id
            name
            registrationId
          }
          processType {
            id
            name
          }
          startDate
          isSourceProcess
          countryCode {
            id
            name
          }
          updatedAt
          processGroup
        }
        processHistory {
          countryCode
          createdAt
          dateOverride
          earliestStartDate
          id
          latestEndDate
          latestSubmissionDate
          owned
          premisesIdentifier
          premisesName
          premisesRegistrationId
          processCategory
          processCode
          processName
          productItemId
          lastProcessStartDate
          lastProcessEndDate
        }
        classification {
          classificationCode
          classificationName
          createdAt
          fieldUniqueId
          id
          itemCode
          updatedAt
          value
        }
        containers {
          number
          seals
          id
        }
        productStatement
        productReference
        sources {
          id
          sequenceNumber
          itemDescription {
            id
            name
          }
          transferRequestId
          transferRequestNumber
          isSourceOf
          processes {
            startDate
            endDate
            foreignPremises
            foreignPremisesIdentifier
            id
            isSourceProcess
            overrideProcessDate
            premises {
              id
              name
            }
            processType {
              id
              name
            }
            updatedAt
            processGroup
          }
          eligibilityCountries {
            id
            name
          }
          attributes {
            netVolume {
              unit {
                id
                name
              }
              value
            }
            netWeight {
              unit {
                id
                name
              }
              value
            }
            inspectionType {
              id
              name
            }
            intendedUse {
              id
              name
            }
            origin {
              id
              name
            }
            speciesCommonName {
              id
              name
            }
            scientificName {
              id
              name
            }
            NZHSCode {
              id
              name
            }
            expiryDateFrom
            expiryDateTo
          }
          classification {
            classificationCode
            classificationName
            createdAt
            fieldUniqueId
            id
            itemCode
            updatedAt
            value
          }
          productStatement
          productReference
        }
        eligibilityCountries {
          id
          name
        }
        attributes {
          productionBatchIds
          temperature {
            id
            name
          }
          netVolume {
            unit {
              id
              name
            }
            value
          }
          netWeight {
            unit {
              id
              name
            }
            value
          }
          grossVolume {
            unit {
              id
              name
            }
            value
          }
          grossWeight {
            unit {
              id
              name
            }
            value
          }
          inspectionType {
            id
            name
          }
          intendedUse {
            id
            name
          }
          origin {
            id
            name
          }
          speciesCommonName {
            id
            name
          }
          scientificName {
            id
            name
          }
          NZHSCode {
            id
            name
          }
          expiryDateFrom
          expiryDateTo
        }
        packaging {
          quantity
          itemMarksOuter
          packageType {
            id
            name
          }
          level
          nominalGrossVolume {
            unit {
              id
              name
            }
            value
          }
          nominalGrossWeight {
            unit {
              id
              name
            }
            value
          }
        }
        supportingDocuments {
          id
          documentNumber
          description
          status
          originCountry {
            id
            name
          }
          documentReferenceNumber
          comment
          productDescription
          isInherited
        }
      }
    }
  }
`)

export const getApprovedProductDetails = graphql(`
  query GetApprovedProductDetails($productIds: [String!]!) {
    getApprovedProductDetails(input: { productIds: $productIds }) {
      products {
        id
        itemDescription {
          id
          name
        }
        attributes {
          origin {
            id
            name
          }
          netWeight {
            value
            unit {
              id
              name
            }
          }
          netVolume {
            value
            unit {
              id
              name
            }
          }
          intendedUse {
            id
            name
          }
          inspectionType {
            id
            name
          }
          NZHSCode {
            id
            name
          }
          speciesCommonName {
            id
            name
          }
          scientificName {
            id
            name
          }
          commonName {
            id
            name
          }
          expiryDateTo
          expiryDateFrom
        }
        eligibilityCountries {
          id
          name
        }
        processes {
          id
          premises {
            id
            name
          }
          processType {
            id
            name
          }
          startDate
          endDate
          foreignPremises
          foreignPremisesIdentifier
          overrideProcessDate
        }
        sources {
          id
          sequenceNumber
          itemDescription {
            id
            name
          }
          transferRequestId
          transferRequestNumber
          isSourceOf
          processes {
            endDate
            id
            foreignPremises
            foreignPremisesIdentifier
            isSourceProcess
            overrideProcessDate
            premises {
              id
              name
            }
            processType {
              id
              name
            }
            startDate
          }
        }
        classification {
          classificationCode
          classificationName
          createdAt
          fieldUniqueId
          id
          itemCode
          updatedAt
          value
        }
        packaging {
          outermostNumberOfPacakges
          itemMarksOuter
          itemMarksOuterCaption
          quantity
          level
          packageType {
            id
            name
          }
          nominalGrossWeight {
            value
            unit {
              id
              name
            }
          }
          nominalGrossVolume {
            value
            unit {
              id
              name
            }
          }
        }
        transferRequestId
        transferRequestNumber
        sequenceNumber
      }
    }
  }
`)

export const getDfeFormConfig = graphql(`
  query GetDfeFormConfig($templateId: ID!, $templateVersionId: ID!) {
    getDfeForm(
      input: { templateId: $templateId, templateVersionId: $templateVersionId }
    ) {
      config
    }
  }
`)

export const getActiveTemplateBySubmissionLayout = graphql(`
  query getActiveTemplateBySubmissionLayout(
    $submissionLayout: SubmissionLayoutType!
  ) {
    getActiveTemplateBySubmissionLayout(submissionLayout: $submissionLayout) {
      templateId
      templateVersionId
    }
  }
`)

export const getRequestPartyRoles = graphql(`
  query FetchRequestPartyRoles(
    $requestId: ID!
    $requestType: CoreRequestType!
  ) {
    fetchRequestPartyRoles(
      input: { requestId: $requestId, requestType: $requestType }
    ) {
      highestPartyRole
      partyRoles
    }
  }
`)

export const getTemplateWithApprovedVersion = graphql(`
  query GetTemplateWithApprovedVersion($templateId: ID!) {
    getTemplateWithActiveApproved(templateId: $templateId) {
      id
      approvedVersion {
        id
      }
    }
  }
`)

export const getExportRequestDetails = graphql(`
  query GetExportRequestDetails($id: ID!) {
    getExportReqDetails(input: { id: $id }) {
      templateId
      template {
        id
      }
      version
      attributes {
        address {
          id
          name
        }
        phytoSchemeControl {
          id
          name
        }
        phytoCertSubType {
          id
          name
        }
        phytoStatementOfRelabelling
        inspectionDate
        letterOfCreditRefCode {
          id
          name
        }
        phytoPackhouse
        phytoLotRefNumber
        importPermitNumbers
        orgCertRefCode
        issuingAuthority {
          id
          name
        }
        halalAuditor {
          id
          name
        }
        brandName {
          id
          name
        }
        numberOfPackagesContainers {
          id
          name
        }
      }
      printDetails {
        officialPrinted
        reasonForReprinting
      }
      certificateNumber
      requiresAttention
      id
      replacedById
      replacesId
      certificateNumberReplacedById
      certificateNumberReplacesId
      overseasAuthorityInvolved
      consignor {
        id
        name
        addresses {
          city
          country
          countryCode
          lines
          postCode
          state
        }
        registrationId
      }
      requestName
      status
      substatus
      reasonForStatusChange
      reasonCategories
      replacedById
      overseasAuthorityInvolved
      urgency {
        id
        name
      }
      commodity {
        id
      }
      destinationCountry {
        id
        name
      }
      verifyingAgencyId
      officialStatement
      additionalDeclaration
      externalConsignor {
        address {
          city
          lines
          postCode
          countryCode {
            id
            name
          }
        }
        id
        name
      }
      importer {
        name
        address {
          city
          postCode
          countryCode {
            id
            name
          }
          lines
          state
        }
        id
        representative
      }
      transitCountries {
        id
        name
      }
      destinationParty {
        name
        id
        address {
          city
          country {
            id
            name
          }
          state
          postCode
          lines
          countryCode {
            name
            id
          }
        }
      }
      transport {
        transportMode {
          id
          name
        }
        loadingPort {
          id
          name
        }
        inspectionPort {
          id
          name
        }
        finalDestination {
          id
          name
        }
        dischargePort {
          id
          name
        }
        departureDate
        conveyanceReference
        carrierName
        expectedArrivalDate
      }
      customsTransitAgent {
        id
        name
        address {
          lines
          city
          countryCode {
            id
            name
          }
          postCode
          state
        }
      }
      supportingDocuments {
        id
        b2gFile {
          extensionType
          name
        }
        comment
        documentReferenceNumber
      }
      printDetails {
        officialPrinted
        reasonForReprinting
      }
      thirdPartyConsignor
      thirdParty {
        id
        name
        registrationId
      }
      unofficialInformation
      uniqueConsignmentReference
      officialStatement
      deliveryDispatchDetails {
        id
        name
      }
      deliveryDetails {
        id
        name
      }
      signingOffice {
        id
        name
      }
      approvedCertificateEmailAddresses
      productItemGroupDetails {
        products {
          id
          productDeclaration
          itemDescription {
            id
            name
          }
          exhausted
          classification {
            classificationCode
            classificationName
            createdAt
            fieldUniqueId
            id
            itemCode
            updatedAt
            value
          }
          sequenceNumber
          attributes {
            originCountries {
              id
              name
            }
            overseasCertIDs
            phytoIsInspectionOnOriginal
            phytoIsOriginalCert
            phytoIsOriginalContainer
            phytoIsRepacked
            phytoPackhouse
            speciesCommonName {
              id
              name
            }
            expiryDateFrom
            expiryDateTo
            productionBatchIds
            temperature {
              id
              name
            }
            NZHSCode {
              id
              name
            }
            intendedUse {
              id
              name
            }
            grossVolume {
              unit {
                id
                name
              }
              value
            }
            grossWeight {
              unit {
                id
                name
              }
              value
            }
            netVolume {
              unit {
                id
                name
              }
              value
            }
            netWeight {
              unit {
                id
                name
              }
              value
            }
            origin {
              id
              name
            }
            phytoGrowers
            phytoBotanicalName {
              id
              name
            }
            phytoCommonName {
              id
              name
            }
            phytoClass {
              id
              name
            }
            phytoSubClass {
              id
              name
            }
            phytoEndPointInspectionRef
            phytoSupplier
            phytoInspectionLocation
            phytoObservation
            phytoVariety
            phytoApprovedSeedCategory {
              id
              name
            }
            phytoLableDescription {
              id
              name
            }
            phytoQuantityMeasure {
              unit {
                id
                name
              }
              value
            }
            phytoQuantityType {
              id
              name
            }
            phytoSeedLotReference
          }
          productStatement
          productReference
          tradeLineGroupNumber
          quantity {
            value
            unit {
              id
              name
            }
          }
          containers {
            number
            seals
            id
          }
          packaging {
            quantity
            level
            itemMarksOuter
            packageType {
              id
              name
            }
            nominalGrossWeight {
              unit {
                id
                name
              }
              value
            }
            nominalGrossVolume {
              unit {
                id
                name
              }
              value
            }
          }
          processes {
            id
            sequenceNumber
            isSourceProcess
            processType {
              id
              name
            }
            premises {
              id
              name
            }
            foreignPremises
            foreignPremisesIdentifier
            endDate
            startDate
            overrideProcessDate
            countryCode {
              id
              name
            }
            updatedAt
            processGroup
            attributes {
              additionalTreatment
              chemicalDetail
              concentration
              duration {
                unit {
                  id
                  name
                }
                value
              }
              temperature {
                value
                unit {
                  id
                  name
                }
              }
            }
          }
          processHistory {
            countryCode
            createdAt
            dateOverride
            earliestStartDate
            id
            latestEndDate
            latestSubmissionDate
            owned
            premisesIdentifier
            premisesName
            premisesRegistrationId
            processCategory
            processCode
            processName
            productItemId
            lastProcessStartDate
            lastProcessEndDate
          }
          sources {
            id
            sequenceNumber
            itemDescription {
              id
              name
            }
            transferRequestId
            transferRequestNumber
            productStatement
            productReference
            isSourceOf
            attributes {
              NZHSCode {
                id
                name
              }
              commonName {
                id
                name
              }
              intendedUse {
                id
                name
              }
              origin {
                id
                name
              }
              netWeight {
                unit {
                  id
                  name
                }
                value
              }
              netVolume {
                unit {
                  id
                  name
                }
                value
              }
              inspectionType {
                id
                name
              }
              expiryDateFrom
              expiryDateTo
            }
            eligibilityCountries {
              id
              name
            }
            processes {
              endDate
              id
              foreignPremises
              isSourceProcess
              overrideProcessDate
              premises {
                id
                name
              }
              processType {
                id
                name
              }
              startDate
              updatedAt
              processGroup
            }
          }
        }
      }
      declarations {
        id
        selected
        statementCode
        inputs {
          id
          inputValue
          sequenceNumber
        }
      }
      sourceTransfer {
        sourceTransferId
        requestNumber
        approvalDate
      }
    }
  }
`)

export const getUserInfo = graphql(`
  query getUserInfo {
    getUserInfo {
      registrations
    }
  }
`)

export const getExportRequestHistory = graphql(`
  query GetExportRequestHistory($input: ExportRequestHistoryInput!) {
    getExportRequestHistory(input: $input) {
      id
      exportRequestId
      action
      nzxmlURI
      overseasAuthorityInvolved
      reasonCategories
      reasonForStatusChange
      reasonForReprinting
      replacesId
      status
      substatus
      updatedAt
      templateVersionId
      updatedBy
      updatedBySystem
      updatedByProfileName
      updatedByFullName
    }
  }
`)

export const getSnapshotPresignedUrl = graphql(`
  query GetSnapshotPresignedUrl($historyId: ID!) {
    getSnapshotPresignedUrl(historyId: $historyId) {
      url
    }
  }
`)

export const getDraftCertificateGenerationStatus = graphql(`
  query getDraftCertificateGenerationStatus(
    $input: GetDraftCertificateGenerationStatusInput!
  ) {
    getDraftCertificateGenerationStatus(input: $input) {
      error
      isCompleted
      pdfCertificateUrl
      status
    }
  }
`)

export const downloadSubmissionXMLQuery = graphql(`
  query downloadSubmissionXML($id: ID!, $requestType: CoreRequestType!) {
    downloadSubmissionXML(requestId: $id, requestType: $requestType) {
      fileName
      url
    }
  }
`)

export const checkUserAccessQuery = graphql(`
  query checkUserAccess($input: CheckUserAccessInput!) {
    checkUserAccess(input: $input) {
      allowed
      message
    }
  }
`)

export const getConsignorDetails = graphql(`
  query getConsignorDetails($id: ID!) {
    getConsignorDetails(id: $id) {
      physicalAddress
      name
      identifiers {
        id
        status
        type
      }
    }
  }
`)

export const getSupportingDocumentDetails = graphql(`
  query GetSupportingDocumentDetails(
    $input: GetSupportingDocumentDetailsInput!
  ) {
    getSupportingDocumentDetails(input: $input) {
      id
      status
      documentType
      documentNumber
      documentReferenceNumber
      fileId
      filename
      createdBy
      createdAt
      updatedBy
      updatedAt
      comment
      primaryOperator {
        id
        name
      }
      intendedOperators {
        id
        name
      }
      originCountry {
        id
        name
      }
      productDescription
      identificationMarks
      foreignManufacturePremises
      foreignPremisesId
      nzManufacturePremises
      productionDates {
        from
        to
      }
    }
  }
`)

export const getSupportingDocumentPresignedUrl = graphql(`
  query GetSupportingDocumentPresignedUrl($input: SupportingDocumentInput!) {
    getSupportingDocumentPresignedUrl(input: $input) {
      presignedUrl
      expiresIn
    }
  }
`)
